import { PaymentStatus } from '@repo/database/browser';
/**
 * Importo massimo, in euro, di un singolo bonifico SEPA disposto tramite il
 * provider di pagamento (ACube open banking): sopra questa soglia il provider
 * rifiuta la disposizione. È l'unica fonte del limite per backend (DTO e
 * service) e webapp (schema del form): se ACube cambia la soglia, si aggiorna
 * solo qui.
 */
export const SEPA_TRANSFER_MAX_AMOUNT = 30_000;
const CENTS_PER_EURO = 100;
const SEPA_AMOUNT_FRACTION_DIGITS = 2;
const toCents = (amount) => Math.round(amount * CENTS_PER_EURO);
/**
 * Vera se l'importo sta entro il limite per singolo bonifico, soglia compresa.
 * Il confronto è al centesimo: una somma di residui in virgola mobile che
 * arriva esattamente alla soglia (può valere `30000.000000000004`) non deve
 * risultare fuori limite.
 */
export const isWithinSepaTransferLimit = (amount) => toCents(amount) <= toCents(SEPA_TRANSFER_MAX_AMOUNT);
/**
 * Importo nel formato che l'API dei pagamenti accetta: arrotondato al
 * centesimo e sempre con due decimali (`30000` → `"30000.00"`). Una somma di
 * residui in virgola mobile (`30000.000000000004`) passata con `toString()`
 * avrebbe troppi decimali e verrebbe rifiutata. L'arrotondamento è lo stesso
 * di {@link isWithinSepaTransferLimit}: l'importo inviato è quello validato.
 */
export const toSepaAmountString = (amount) => (toCents(amount) / CENTS_PER_EURO).toFixed(SEPA_AMOUNT_FRACTION_DIGITS);
/**
 * Età minima, in giorni, di un bonifico PENDING perché il backoffice possa
 * reimpostarlo (cancellarlo) quando il provider lo dà ancora in attesa: prima
 * di questa soglia il provider potrebbe ancora eseguirlo, e cancellarlo
 * lascerebbe un addebito senza traccia. Il vincolo di età non si applica solo
 * se il provider dà il bonifico FAILED o dichiara di non conoscerlo; uno stato
 * remoto non riconosciuto, o un provider che non risponde, valgono come
 * ancora in attesa. Uno stato remoto noto non terminale (bonifico in corso)
 * blocca il reset a qualsiasi età.
 */
export const SEPA_PENDING_RESET_MIN_AGE_DAYS = 7;
/**
 * Giorni oltre i quali un bonifico PENDING che il provider dà ancora in corso
 * (stato remoto noto non terminale, es. `accepted`) è un'anomalia: il sync lo
 * segnala nel registro errori con una riga per tenant e giro. Non sblocca
 * nulla: FAILED e reset restano vietati finché il provider non chiude il
 * pagamento (lo può confermare solo l'operatore).
 */
export const SEPA_REMOTE_IN_PROGRESS_ALERT_DAYS = 7;
/**
 * Finestra letta di default dal sync pull dei pagamenti ACube, in giorni (come
 * il registro dei job). `createdAt[after]` va SEMPRE passato esplicitamente:
 * senza, ACube restituisce solo i pagamenti del mese corrente. Condivisa col
 * backoffice, che la mostra nel dialog di sincronizzazione.
 */
export const ACUBE_PAYMENTS_SYNC_WINDOW_DAYS = 90;
/**
 * Filtro "stato non riconosciuto" dello specchio dei pagamenti ACube: le
 * righe il cui stato remoto il mapper non mappa (`mappedStatus` null). Qui e
 * non in `@repo/api` perché il backoffice lo usa a runtime, e da `@repo/api`
 * (CommonJS) importa solo tipi.
 */
export const ACUBE_PAYMENT_UNKNOWN_STATUS = 'UNKNOWN';
/**
 * Il filtro di ciascun tab di Pagamenti SEPA che legge lo specchio ACube,
 * per chiave del contatore di `GET backoffice/acube-payments/tab-counts`.
 * Unica fonte per il backend (contatori) e il backoffice (lista del tab):
 * se divergessero, il numero del tab non tornerebbe con la lista.
 * Il mapper non produce mai `mappedStatus` PENDING (uno stato remoto non
 * terminale resta non mappato): «In attesa» è il bonifico collegato.
 */
export const ACUBE_PAYMENT_TAB_FILTERS = {
    all: {},
    pending: { localStatus: PaymentStatus.PENDING },
    confirmed: { mappedStatus: PaymentStatus.CONFIRMED },
    failed: { mappedStatus: PaymentStatus.FAILED },
    unknown: { mappedStatus: ACUBE_PAYMENT_UNKNOWN_STATUS },
    manual: { manual: true },
};
/** Lunghezza massima della nota obbligatoria delle azioni admin sui bonifici. */
export const SEPA_ADMIN_NOTE_MAX_LENGTH = 1000;
/**
 * Colonne ordinabili delle due liste del backoffice pagamenti: le stesse per
 * la whitelist del backend (un campo arbitrario non arriva a Prisma) e per le
 * colonne della tabella, che non propongono un ordinamento che il server
 * scarterebbe.
 */
export const ACUBE_PAYMENTS_SORTABLE_FIELDS = [
    'acubeCreatedAt',
    'amount',
    'direction',
    'mappedStatus',
    'status',
];
export const SEPA_PAYMENTS_ADMIN_SORTABLE_FIELDS = [
    'createdAt',
    'amount',
    'status',
];
/**
 * Codici motivo ISO 20022 (ExternalStatusReason1Code) con cui le banche
 * spiegano il rifiuto o il reso di un bonifico SEPA. L'elenco è chiuso: un
 * codice si riconosce solo se è fra questi, così una parola di quattro
 * caratteri nel messaggio del provider non diventa un motivo inventato.
 * La webapp ne traduce la descrizione (`payments.failure_reasons.<codice>`).
 */
export const SEPA_FAILURE_REASON_CODES = [
    'AC01',
    'AC04',
    'AC06',
    'AC13',
    'AG01',
    'AG02',
    'AM04',
    'AM05',
    'AM09',
    'BE04',
    'CUST',
    'DUPL',
    'FF01',
    'FOCR',
    'MD07',
    'MS02',
    'MS03',
    'RC01',
    'RR01',
    'RR02',
    'RR03',
    'RR04',
    'TM01',
];
const SEPA_FAILURE_REASON_PATTERN = new RegExp(`(?:^|[^A-Z0-9])(${SEPA_FAILURE_REASON_CODES.join('|')})(?![A-Z0-9])`);
/**
 * Codice motivo del rifiuto contenuto nel messaggio d'errore del provider
 * (`"BankRejected: AC01 invalid account"` → `AC01`), oppure `null` se il
 * messaggio non ne cita uno noto. Il confronto è sulle maiuscole del
 * messaggio: i codici sono sempre scritti così nei tracciati.
 */
export function extractSepaFailureReasonCode(message) {
    if (!message)
        return null;
    const match = SEPA_FAILURE_REASON_PATTERN.exec(message);
    return match?.[1] ?? null;
}
/** Vero se `code` è un codice motivo che la webapp sa descrivere. */
export function isSepaFailureReasonCode(code) {
    return (!!code && SEPA_FAILURE_REASON_CODES.includes(code));
}
//# sourceMappingURL=sepa-payments.js.map